import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { X, Plus, Target, CornerDownLeft } from 'lucide-react';
import { toast } from 'sonner';
import { createMit, createAzione, deleteMit, deleteAzione, fetchDashboard } from '../../lib/api';
import { useFocusTrap } from '../../lib/useFocusTrap';

/**
 * Quick Add modal — pulsante "+" + scorciatoia Shift+N.
 *
 * Preferenze memorizzate in localStorage (`qp.quick_add_prefs`):
 *   - lastKind: ultima scelta MIT vs Azione
 *   - lastKrId: ultimo KR collegato (riproposto solo se l'utente ha 1 obiettivo aperto)
 *
 * Auto-detect contesto: se siamo su `/obiettivo/<id>`, il modale propone come
 * default un KR di quell'obiettivo (e tipo "Azione" che è più comune lì).
 *
 * Eventi pubblicati:
 *   `qp:quick-add-created` — { kind: 'mit'|'azione', row }
 */
const PREFS_KEY = 'qp.quick_add_prefs';

function loadPrefs() {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return {};
    return JSON.parse(raw) || {};
  } catch { return {}; }
}
function savePrefs(p) {
  try { localStorage.setItem(PREFS_KEY, JSON.stringify(p)); } catch { /* quota */ }
}
function isoWeekToday() {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const wn = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(wn).padStart(2, '0')}`;
}

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function emitQuickAddCreated(kind, row) {
  window.dispatchEvent(new CustomEvent('qp:quick-add-created', { detail: { kind, row } }));
}

export default function QuickAdd() {
  const location = useLocation();
  const prefsRef = useRef(loadPrefs());
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState(prefsRef.current.lastKind || 'mit');
  const [testo, setTesto] = useState('');
  const [dataIso, setDataIso] = useState(todayIso());
  const [settimana, setSettimana] = useState(isoWeekToday());
  const [krOptions, setKrOptions] = useState([]);
  const [krId, setKrId] = useState('');
  const [isBau, setIsBau] = useState(false);
  // Se pre-impostato via evento qp:quick-add-open con {kind:'azione', dataIso},
  // l'azione viene fissata a quel giorno (payload createAzione include `data`).
  // Non-null → la UI mostra un chip "Fissata al giorno · YYYY-MM-DD" con X per unpin.
  const [azionePinnedDate, setAzionePinnedDate] = useState(null);
  const [filterByCurrentArea, setFilterByCurrentArea] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);
  const panelRef = useRef(null);

  // Focus trap nel pannello del modale. Niente onEscape (Esc è già gestito
  // dal listener globale del componente) e initialFocus:false (resta
  // l'autofocus esistente sull'input).
  useFocusTrap(open, panelRef, { initialFocus: false });

  // Aree attive dal querystring `?area=business,lavoro` della pagina corrente.
  // Se presenti, filtriamo il select per ridurre errori di collegamento.
  const currentAreas = (() => {
    const sp = new URLSearchParams(location.search);
    const raw = (sp.get('area') || '').trim();
    return raw ? raw.split(',').map((a) => a.toLowerCase()) : [];
  })();

  // KR filtrati per area corrente (se attiva e l'utente ha richiesto il filtro)
  const visibleKrOptions = (currentAreas.length > 0 && filterByCurrentArea)
    ? krOptions.filter((k) => currentAreas.includes((k.area || '').toLowerCase()))
    : krOptions;

  // Scorciatoia globale: Shift+N (fuori da input). Il vecchio "n" singolo
  // scattava troppo facilmente; il vecchio ⌘⇧N collideva con "Nuova finestra
  // incognito" di Chrome — entrambi sostituiti (lug 2026).
  // Plus evento custom 'qp:quick-add-open' per apertura da bottoni esterni.
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toUpperCase();
      const isInput = ['INPUT', 'TEXTAREA', 'SELECT'].includes(tag)
        || e.target?.isContentEditable;

      // Shift + N senza meta/ctrl/alt, fuori da input
      if (!isInput
        && (e.key === 'N' || e.key === 'n')
        && e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        setOpen(true);
        return;
      }

      if (e.key === 'Escape' && open) setOpen(false);
    };
    const onOpen = (e) => {
      // Accetta detail opzionale per pre-impostare contesto:
      //   { kind: 'mit'|'azione', dataIso, settimana, krId, obiettivoId }
      const ctx = e?.detail || {};
      const nextKind = (ctx.kind === 'mit' || ctx.kind === 'azione') ? ctx.kind : null;
      if (nextKind) setKind(nextKind);
      if (ctx.dataIso) {
        setDataIso(ctx.dataIso);
        // Se è un'azione con dataIso esplicito, la fissiamo al giorno
        if (nextKind === 'azione') setAzionePinnedDate(ctx.dataIso);
        // Calcola settimana ISO della data se non passata
        if (!ctx.settimana) {
          try {
            const d = new Date(ctx.dataIso + 'T00:00:00');
            d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
            const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
            const wn = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
            setSettimana(`${d.getUTCFullYear()}-W${String(wn).padStart(2, '0')}`);
          } catch { /* ignore */ }
        }
      } else if (nextKind === 'azione') {
        // Cambio kind ad azione senza data → nessun pin (reset stato precedente)
        setAzionePinnedDate(null);
      }
      if (ctx.settimana) setSettimana(ctx.settimana);
      if (ctx.krId) setKrId(ctx.krId);
      setOpen(true);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('qp:quick-add-open', onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('qp:quick-add-open', onOpen);
    };
  }, [open]);

  // Carica la lista dei KR quando il modale si apre (per il select)
  useEffect(() => {
    if (!open || krOptions.length > 0) return;
    fetchDashboard()
      .then((d) => {
        const opts = [];
        for (const ob of (d.obiettivi || [])) {
          for (const kr of (ob.key_results || [])) {
            opts.push({
              id: kr.id,
              titolo: kr.titolo,
              area: ob.area,
              obiettivo_id: ob.id,
              obiettivo_titolo: ob.titolo,
            });
          }
        }
        setKrOptions(opts);
      })
      .catch(() => { /* silent */ });
  }, [open, krOptions.length]);

  // Quando si apre il modale, applica i default in base al contesto:
  // 1. Se la pagina è /obiettivo/<id> → propone "Azione" + KR di quell'obiettivo
  // 2. Altrimenti riprende l'ultimo `kind` da localStorage; il KR resta libero
  useEffect(() => {
    if (!open) return;
    const prefs = loadPrefs();
    const m = location.pathname.match(/^\/obiettivo\/([^/]+)/);
    if (m && krOptions.length > 0) {
      // Cerca primo KR di questo obiettivo
      const obId = m[1];
      const kr = krOptions.find((k) => k.obiettivo_id === obId);
      if (kr) {
        setKind('azione'); // default più comune in contesto obiettivo
        setKrId(kr.id);
        return;
      }
    }
    if (prefs.lastKind) setKind(prefs.lastKind);
  }, [open, krOptions, location.pathname]);

  // Autofocus quando si apre
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  const reset = () => {
    setTesto('');
    setDataIso(todayIso());
    setSettimana(isoWeekToday());
    setKrId('');
    setIsBau(false);
    setAzionePinnedDate(null);
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!testo.trim() || submitting) return;
    setSubmitting(true);
    try {
      let row;
      if (kind === 'mit') {
        const res = await createMit({
          testo: testo.trim(),
          data: dataIso,
          key_result_id: krId || null,
        });
        row = res.data;
        // Toast con undo
        toast(`MIT creata · ${dataIso}`, {
          duration: 6000,
          action: {
            label: 'Annulla',
            onClick: async () => {
              try {
                await deleteMit(row.id);
                emitQuickAddCreated('mit:deleted', row);
                toast.success('MIT eliminata');
              } catch {
                toast.error('Undo fallito');
              }
            },
          },
        });
      } else {
        const inferredArea = krOptions.find((k) => k.id === krId)?.area || null;
        const res = await createAzione({
          testo: testo.trim(),
          settimana,
          data: azionePinnedDate || null,
          key_result_id: krId || null,
          area: inferredArea,
          is_bau: isBau,
        });
        row = res.data;
        toast(azionePinnedDate ? `Azione creata · fissata al ${azionePinnedDate}` : `Azione creata · ${settimana}`, {
          duration: 6000,
          action: {
            label: 'Annulla',
            onClick: async () => {
              try {
                await deleteAzione(row.id);
                emitQuickAddCreated('azione:deleted', row);
                toast.success('Azione eliminata');
              } catch {
                toast.error('Undo fallito');
              }
            },
          },
        });
      }
      emitQuickAddCreated(kind, row);
      // Salva l'ultima scelta come preferenza utente
      savePrefs({ lastKind: kind });
      reset();
      setOpen(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'errore';
      toast.error(`Creazione fallita · ${msg}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating "+" button — bottom-right, sopra il badge Emergent */}
      <button
        onClick={() => setOpen(true)}
        className="group fixed bottom-24 right-6 z-30 w-12 h-12 rounded-full bg-ink text-canvas shadow-lg hover:scale-105 transition-transform flex items-center justify-center"
        title="Crea MIT o azione (premi ⇧N)"
        aria-label="Quick add"
        data-testid="quick-add-fab"
      >
        <Plus size={20} strokeWidth={2} />
        <span
          className="absolute -top-1.5 -right-1.5 font-mono text-[9px] uppercase tracking-[0.1em] bg-linesoft text-ink px-1.5 py-0.5 rounded-full border border-line opacity-0 group-hover:opacity-100 transition-opacity"
          aria-hidden="true"
        >
          N
        </span>
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] px-4 bg-black/30 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid="quick-add-modal"
        >
          <form
            ref={panelRef}
            onSubmit={submit}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg bg-canvas border border-ink shadow-2xl"
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-line">
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-soft">
                Quick add · premi <kbd className="mx-0.5 px-1 py-px text-[9px] bg-line">⇧N</kbd>
              </span>
              <button type="button" onClick={() => setOpen(false)} className="text-mute hover:text-ink" aria-label="Chiudi">
                <X size={16} strokeWidth={1.5} />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Tipo */}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setKind('mit')}
                  className={
                    'flex-1 font-mono text-[11px] uppercase tracking-[0.18em] px-3 py-2 border transition-colors ' +
                    (kind === 'mit'
                      ? 'border-ink bg-ink text-canvas'
                      : 'border-line text-soft hover:border-ink hover:text-ink')
                  }
                  data-testid="quick-add-type-mit"
                >
                  MIT del giorno
                </button>
                <button
                  type="button"
                  onClick={() => setKind('azione')}
                  className={
                    'flex-1 font-mono text-[11px] uppercase tracking-[0.18em] px-3 py-2 border transition-colors ' +
                    (kind === 'azione'
                      ? 'border-ink bg-ink text-canvas'
                      : 'border-line text-soft hover:border-ink hover:text-ink')
                  }
                  data-testid="quick-add-type-azione"
                >
                  Azione settimanale
                </button>
              </div>

              {/* Testo */}
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-soft block mb-1">Testo</span>
                <input
                  ref={inputRef}
                  type="text"
                  value={testo}
                  onChange={(e) => setTesto(e.target.value)}
                  placeholder={kind === 'mit' ? 'Es. Riunione kickoff progetto' : 'Es. Pubblicare 1 articolo'}
                  className="w-full px-3 py-2.5 border border-line focus:border-ink focus:outline-none font-body text-sm"
                  required
                  data-testid="quick-add-testo"
                />
              </label>

              {/* Data o Settimana */}
              {kind === 'mit' ? (
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-soft block mb-1">Data</span>
                  <input
                    type="date"
                    value={dataIso}
                    onChange={(e) => setDataIso(e.target.value)}
                    className="w-full px-3 py-2.5 border border-line focus:border-ink focus:outline-none font-mono text-sm"
                    data-testid="quick-add-data"
                  />
                </label>
              ) : (
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-soft block mb-1">Settimana ISO</span>
                  <input
                    type="text"
                    value={settimana}
                    onChange={(e) => setSettimana(e.target.value)}
                    pattern="\d{4}-W\d{2}"
                    placeholder="2026-W24"
                    className="w-full px-3 py-2.5 border border-line focus:border-ink focus:outline-none font-mono text-sm"
                    data-testid="quick-add-settimana"
                  />
                  {azionePinnedDate && (
                    <div
                      className="mt-2 flex items-center gap-2 px-2.5 py-1.5 border border-ink bg-canvas"
                      data-testid="quick-add-azione-pinned"
                    >
                      <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-soft">
                        Fissata al giorno
                      </span>
                      <span className="font-mono text-xs tabular-nums text-ink flex-1" data-testid="quick-add-data-chip">
                        {azionePinnedDate}
                      </span>
                      <button
                        type="button"
                        onClick={() => setAzionePinnedDate(null)}
                        className="font-mono text-[10px] uppercase tracking-[0.15em] text-mute hover:text-ink"
                        title="Rimuovi il pin — l'azione resterà flottante sulla settimana"
                        data-testid="quick-add-azione-unpin"
                      >
                        Rimuovi
                      </button>
                    </div>
                  )}
                </label>
              )}

              {/* Collegamento KR */}
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-soft flex items-center justify-between mb-1">
                  <span>Collega a un Key Result <span className="lowercase tracking-normal">(opzionale)</span></span>
                  {currentAreas.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setFilterByCurrentArea((v) => !v)}
                      className={'lowercase tracking-normal px-1.5 py-0.5 border text-[10px] ' + (filterByCurrentArea ? 'border-ink bg-ink text-canvas' : 'border-line text-soft hover:border-ink')}
                      data-testid="quick-add-area-filter-toggle"
                      title="Filtra il select per le aree attive nella vista corrente"
                    >
                      {filterByCurrentArea
                        ? `Solo ${currentAreas.join(', ')} (${visibleKrOptions.length})`
                        : `Mostra tutti (${krOptions.length})`}
                    </button>
                  )}
                </span>
                <select
                  value={krId}
                  onChange={(e) => setKrId(e.target.value)}
                  className="w-full px-3 py-2.5 border border-line focus:border-ink focus:outline-none font-body text-sm bg-canvas"
                  data-testid="quick-add-kr"
                >
                  <option value="">— Nessun KR (libero) —</option>
                  {(() => {
                    // Raggruppa KR per obiettivo (UX più chiaro con molti KR)
                    const byOb = {};
                    for (const kr of visibleKrOptions) {
                      const key = kr.obiettivo_id || '__noob__';
                      if (!byOb[key]) byOb[key] = { titolo: kr.obiettivo_titolo || 'Senza obiettivo', area: kr.area, krs: [] };
                      byOb[key].krs.push(kr);
                    }
                    return Object.entries(byOb).map(([obId, g]) => (
                      <optgroup key={obId} label={`[${(g.area || '').slice(0, 3).toUpperCase()}] ${g.titolo.slice(0, 60)}`}>
                        {g.krs.map((kr) => (
                          <option key={kr.id} value={kr.id}>
                            {kr.titolo.slice(0, 80)}
                          </option>
                        ))}
                      </optgroup>
                    ));
                  })()}
                </select>
              </label>

              {/* BAU flag per azioni */}
              {kind === 'azione' && (
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isBau}
                    onChange={(e) => setIsBau(e.target.checked)}
                    data-testid="quick-add-bau"
                  />
                  <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-soft">
                    BAU (business-as-usual, non collegata a KR)
                  </span>
                </label>
              )}
            </div>

            <div className="px-5 py-3 border-t border-line flex items-center justify-between bg-tint">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-mute">
                <CornerDownLeft className="inline h-3 w-3" strokeWidth={1.5} /> per salvare · Esc per annullare
              </span>
              <button
                type="submit"
                disabled={!testo.trim() || submitting}
                className="font-mono text-xs uppercase tracking-[0.2em] px-5 py-2 border border-ink bg-ink text-canvas hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity inline-flex items-center gap-2"
                data-testid="quick-add-submit"
              >
                <Target size={13} strokeWidth={1.5} />
                {submitting ? 'Creo…' : 'Crea'}
              </button>
            </div>
          </form>
        </div>,
        document.body,
      )}
    </>
  );
}
